import { useCallback, useRef } from 'react';
import type { SearchSuggestion } from '../../../../app/components/SearchBar/SearchBarAutocomplete';
import { mapHitToSuggestion } from '../../application/mapSearch';
import { useLazySearchQuery } from '../../infrastructure/api/search.api';

/**
 * Header autocomplete — debounce lives in SearchBarAutocomplete;
 * this hook aborts stale fetches via RTK lazy + request seq.
 */
export function useSearchSuggestions() {
  const [trigger] = useLazySearchQuery();
  const seqRef = useRef(0);

  const fetchSuggestions = useCallback(
    async (query: string): Promise<SearchSuggestion[]> => {
      const needle = query.trim();
      if (needle.length < 2) return [];

      const seq = ++seqRef.current;
      try {
        const result = await trigger({
          q: needle,
          limit: 5,
          mode: 'suggest',
        }).unwrap();
        if (seq !== seqRef.current) return [];
        return (result.list ?? []).map((hit) => mapHitToSuggestion(hit));
      } catch {
        if (seq !== seqRef.current) return [];
        return [];
      }
    },
    [trigger],
  );

  return { fetchSuggestions };
}
